import * as Sentry from '@sentry/react'
import { lazy, Suspense } from 'react'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'

import { useCurrentUser } from './api/auth/queries/useCurrentUser'
import { useUserPickupOrders } from './api/pickup-orders/queries/useUserPickupOrders'
import { useUserPkgs } from './api/user-package/queries/useUserPkgs'
import { Spinner } from './components/ui/spinner'
import ScrollToTop from './helpers/ScrollToTop'
import { useOfficePagePermissions } from './hooks/useOfficePagePermissions'
import { usePageTitle } from './hooks/usePageTitle'
import ClientPage from './pages/client-side/ClientPage'
import ClientPkgPage from './pages/client-side/ClientPkg/ClientPkgsPage'
import ForgotPasswordPage from './pages/client-side/Login/ForgotPasswordPage'
import LoginPage from './pages/client-side/Login/LoginPage'
import NativeReturnPage from './pages/client-side/NativeReturn/NativeReturnPage'
import PaymentPage from './pages/client-side/Payment/PaymentPage'
import ProfilePage from './pages/client-side/Profile/ProfilePage'
import ReceiptUploadPage from './pages/client-side/ReceiptUpload/ReceiptUploadPage'
import ReturnPkgPage from './pages/client-side/ReturnPkg/ReturnPkgPage'
import UnitePkgPage from './pages/client-side/UnitePkg/UnitePkgPage'
import WarehouseAddressesPage from './pages/client-side/WarehouseAddresses/WarehouseAddressesPage'
import { OfficeRoutePath, RoutePath } from './RoutePath'

const AddNewPackagePage = lazy(() => import('./pages/office-side/AddNewPackage/AddNewPackagePage'))
const AffiliatePage = lazy(() => import('./pages/office-side/Affiliate/AffiliatePage'))
const BarcodeGunShip = lazy(() => import('./pages/office-side/BarcodeGunShip/BarcodeGunShip'))
const ShelfsPage = lazy(() => import('./pages/office-side/Shelfs/ShelfsPage'))
const CouponOfficePage = lazy(() => import('./pages/office-side/Coupon/CouponOfficePage'))
const CampaignsPage = lazy(() => import('./pages/office-side/Marketing/CampaignsPage'))
const MarketingDashboardPage = lazy(() => import('./pages/office-side/Marketing/MarketingDashboardPage'))
const MarketingInvoicesPage = lazy(() => import('./pages/office-side/Marketing/MarketingInvoicesPage'))
const MarketingAffiliatePackagesPage = lazy(() => import('./pages/office-side/Marketing/MarketingAffiliatePkgsPage'))
const MarketingEmailsPage = lazy(() => import('./pages/office-side/Marketing/MarketingEmailsPage'))
const EditAndUpdatePage = lazy(() => import('./pages/office-side/EditAndUpdate/EditAndUpdatePage'))
const UserPage = lazy(() => import('./pages/office-side/EditAndUpdate/UserPage'))
const Location = lazy(() => import('./pages/office-side/Location/Location'))
const PkgShippingChecklist = lazy(() => import('./pages/office-side/PackageChecklist/PkgShippingChecklist'))
const PackageRequests = lazy(() => import('./pages/office-side/PackageRequests/PkgReqPage'))
const PopularBrandsItems = lazy(() => import('./pages/office-side/PopularBrandsItems/PopularBrandsItems'))
const OfficeShipPageComponent = lazy(() => import('./pages/office-side/Ship/OfficeShipPageComponent'))
const Stats = lazy(() => import('./pages/office-side/Stats/StatsPage'))
const Sticker = lazy(() => import('./pages/office-side/Sticker/Sticker'))
const WarehouseArrivalsPage = lazy(() => import('./pages/office-side/WarehouseArrivals/WarehouseArrivalsPage'))
const DelayedPkgPage = lazy(() => import('./pages/office-side/DelayedPkg/DelayedPkgPage'))
const PickupOrdersPage = lazy(() => import('./pages/office-side/PickupOrders/PickupOrdersPage'))
const ScanPickupOrdersPage = lazy(() => import('./pages/office-side/PickupOrders/ScanPickupOrdersPage'))
const USAPackageLookupPage = lazy(() => import('./pages/office-side/USAPackageLookup/USAPackageLookupPage'))
const PkgCasesPage = lazy(() => import('./pages/office-side/PkgCases/PkgCasesPage'))
const NewPkgCasePage = lazy(() => import('./pages/office-side/PkgCases/NewPkgCasePage'))
const USAPkgLookupsListPage = lazy(() => import('./pages/office-side/USAPkgLookupsList/USAPkgLookupsListPage'))
const AdminSettingsPage = lazy(() => import('./pages/office-side/Admin/AdminSettingsPage'))
const AdminDataPage = lazy(() => import('./pages/office-side/Admin/AdminDataPage'))
const AuditLogPage = lazy(() => import('./pages/office-side/Admin/AuditLogPage'))
const FinanceMicroPage = lazy(() => import('./pages/office-side/Admin/Finance/FinanceMicroPage'))
const FinanceMacroPage = lazy(() => import('./pages/office-side/Admin/Finance/FinanceMacroPage'))
const FinanceSettingsPage = lazy(() => import('./pages/office-side/Admin/Finance/FinanceSettingsPage'))
const ReceiptTextractTestPage = lazy(() => import('./pages/office-side/Admin/ReceiptTextractTestPage'))
const AffiliateManagerPage = lazy(() => import('./pages/office-side/AffiliateManager/AffiliateManagerPage'))
const ServiceReqsPage = lazy(() => import('./pages/office-side/ServiceRequests/ServiceReqsPage'))

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RouteWrapper = ({ Component, ...props }: { Component: React.LazyExoticComponent<React.ComponentType<any>> }) => {
  return (
    <Suspense
      fallback={
        <div className='flex items-center justify-center' style={{ height: '100dvh' }}>
          <Spinner className='size-12' />
        </div>
      }
    >
      <Component {...props} />
    </Suspense>
  )
}

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { data: loggedUser } = useCurrentUser()
  return loggedUser?.id ? <>{children}</> : <LoginPage />
}

// Wrapper for client routes with ClientPage layout
const ClientLayout = () => {
  const { data: loggedUser } = useCurrentUser()
  useUserPkgs()
  useUserPickupOrders()
  if (!loggedUser?.id) return <LoginPage />
  return (
    <ClientPage>
      <Outlet />
    </ClientPage>
  )
}

export default function Layout() {
  const { data: loggedUser } = useCurrentUser()
  const officePermissions = useOfficePagePermissions()
  usePageTitle()
  return (
    <div className='h-[calc(100dvh-var(--navbar-height))]'>
      <ScrollToTop>
        <SentryRoutes>
          <Route path='/welcome' element={<LoginPage />} />

          <Route path={RoutePath.Login} element={<LoginPage />} />

          <Route path={RoutePath.ForgotPassword} element={<ForgotPasswordPage />} />
          <Route path={RoutePath.NativeReturn} element={<NativeReturnPage />} />

          {/* Client routes with shared layout */}
          <Route path={RoutePath.Client} element={<ClientLayout />}>
            <Route path={RoutePath.ClientProfile} element={<ProfilePage />} />
            <Route path={RoutePath.ClientProfilePersonal} element={<ProfilePage />} />
            <Route path={RoutePath.ClientProfileAddresses} element={<ProfilePage />} />
            <Route path={RoutePath.ClientProfileAffiliate} element={<ProfilePage />} />
            <Route path={RoutePath.ClientAddresses} element={<WarehouseAddressesPage />} />
            <Route path={`${RoutePath.ClientPkgReturn}/:pkgId`} element={<ReturnPkgPage />} />
            <Route path={RoutePath.ClientPkg} element={<ClientPkgPage />} />
            <Route path={RoutePath.ClientPkgUnite} element={<UnitePkgPage />} />
          </Route>

          <Route
            path={`${RoutePath.ReceiptUpload}/:pkgId`}
            element={
              <ProtectedRoute>
                <ReceiptUploadPage />
              </ProtectedRoute>
            }
          />

          <Route
            path={`payment/:step/:pkgId`}
            element={
              <ProtectedRoute>
                <PaymentPage />
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.AddNewPkg}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewAddNewPkg && <RouteWrapper Component={AddNewPackagePage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.Ship}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewShip && <RouteWrapper Component={OfficeShipPageComponent} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.EditAndUpdate}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewEditAndUpdate && <RouteWrapper Component={EditAndUpdatePage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path='/office-userPage/:userId'
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewEditAndUpdate && <RouteWrapper Component={UserPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={`${OfficeRoutePath.PkgRequests}/:reqType?/:reqId?`}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewPackageRequests && <RouteWrapper Component={PackageRequests} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.CheckForm}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewPkgChecklist && <RouteWrapper Component={PkgShippingChecklist} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.Stats}
            element={<ProtectedRoute>{loggedUser && officePermissions.canViewStats && <RouteWrapper Component={Stats} />}</ProtectedRoute>}
          />

          <Route
            path={OfficeRoutePath.PopularBrandsItems}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewPopularBrandsItems && <RouteWrapper Component={PopularBrandsItems} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.Affiliate}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewAdminPages && <RouteWrapper Component={AffiliatePage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.Coupon}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewAdminPages && <RouteWrapper Component={CouponOfficePage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.MarketingDashboard}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewAdminPages && <RouteWrapper Component={MarketingDashboardPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.MarketingCampaigns}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewAdminPages && <RouteWrapper Component={CampaignsPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.MarketingInvoices}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewAdminPages && <RouteWrapper Component={MarketingInvoicesPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.MarketingAffiliatePackages}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewAdminPages && <RouteWrapper Component={MarketingAffiliatePackagesPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.MarketingEmails}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewAdminPages && <RouteWrapper Component={MarketingEmailsPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.AdminSettings}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewAdminPages && <RouteWrapper Component={AdminSettingsPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.AdminData}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewAdminPages && <RouteWrapper Component={AdminDataPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.AuditLog}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewAdminPages && <RouteWrapper Component={AuditLogPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.FinanceMicro}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewAdminPages && <RouteWrapper Component={FinanceMicroPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.FinanceMacro}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewAdminPages && <RouteWrapper Component={FinanceMacroPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.FinanceSettings}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewAdminPages && <RouteWrapper Component={FinanceSettingsPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.ReceiptTextractTest}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewAdminPages && <RouteWrapper Component={ReceiptTextractTestPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.AffiliateManager}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewAffiliateManager && <RouteWrapper Component={AffiliateManagerPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.ServiceReqs}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewServiceReqs && <RouteWrapper Component={ServiceReqsPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.BarcodeGunShip}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewBarcodeGunShip && <RouteWrapper Component={BarcodeGunShip} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.Shelfs}
            element={
              <ProtectedRoute>{loggedUser && officePermissions.canViewShelfs && <RouteWrapper Component={ShelfsPage} />}</ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.PkgLocation}
            element={
              <ProtectedRoute>{loggedUser && officePermissions.canViewPkgLocation && <RouteWrapper Component={Location} />}</ProtectedRoute>
            }
          />

          <Route path={OfficeRoutePath.ScanMe} element={<Navigate to={OfficeRoutePath.Shelfs} replace />} />

          <Route
            path={OfficeRoutePath.WarehouseArrivals}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewWarehouseArrivals && <RouteWrapper Component={WarehouseArrivalsPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.PkgLookup}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewPkgLookup && <RouteWrapper Component={USAPackageLookupPage} />}
              </ProtectedRoute>
            }
          />
          <Route
            path={OfficeRoutePath.NewPkgCase}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canCreatePkgCase && <RouteWrapper Component={NewPkgCasePage} />}
              </ProtectedRoute>
            }
          />
          <Route
            path={OfficeRoutePath.PkgCases}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewPkgCases && <RouteWrapper Component={PkgCasesPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.PkgLookupsList}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewPkgLookupsList && <RouteWrapper Component={USAPkgLookupsListPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.DelayedPkgs}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewDelayedPkgs && <RouteWrapper Component={DelayedPkgPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.PickupOrders}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewPickupOrders && <RouteWrapper Component={PickupOrdersPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.ScanPickupOrders}
            element={
              <ProtectedRoute>
                {loggedUser && officePermissions.canViewScanPickupOrders && <RouteWrapper Component={ScanPickupOrdersPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path={OfficeRoutePath.Sticker}
            element={
              <ProtectedRoute>{loggedUser && officePermissions.canViewSticker && <RouteWrapper Component={Sticker} />}</ProtectedRoute>
            }
          />

          <Route path='/' element={<LoginPage />} />
        </SentryRoutes>
      </ScrollToTop>
    </div>
  )
}
